import { useState, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import DigitalDispatchWebPubSubService from 'src/services/DigitalDispatchWebPubSubService';
import { DIGITAL_DISPATCH_LIVE_UPDATE_PROVIDER_ACCEPT } from 'src/constants/digitalDispatchWebPubSub';
import { useAppInsightsContext, useTrackEvent } from '@microsoft/applicationinsights-react-js';

const digitalDispatchWebPubSubService = new DigitalDispatchWebPubSubService();

/**
 * Sets up a connection to an Azure WebPubSub connection
 * group that only listens to updates from the dispatch with
 * the specified dispatchId (Qore dispatch ID).
 * @param {number} dispatchId
 */
const useDigitalDispatchTimerDispatchUpdate = (dispatchId, eventName) => {
  const appInsights = useAppInsightsContext();
  const [isConnected, setIsConnected] = useState(false);
  const [isFetchingClientAccessUri, setIsFetchingClientAccessUri] = useState(true);
  const [providerAcceptanceStatus, setProviderAcceptanceStatus] = useState(null);
  const [webPubSubError, setWebPubSubError] = useState(null);
  const { enqueueSnackbar } = useSnackbar();
  const azureWebPubSubError = useTrackEvent(
    appInsights,
    eventName,
    {
      error: webPubSubError,
      errorMessage: webPubSubError?.message
    }
  );

  useEffect(() => {
    if (webPubSubError) {
      azureWebPubSubError();
    }
  }, [webPubSubError]);

  /**
   * @param {number} dispatchId - The dispatch ID (from Qore);
   * set ups the WebPubSub connection and creates a group for
   * listening for just dispatch status updates (the 1st update
   * to come in indicates a provider has accepted the dispatch offer)
   * @returns {Promise<void>}
   */
  const setUpWebPubSubClientInstance = async () => {
    try {
      setIsFetchingClientAccessUri(true);
      await digitalDispatchWebPubSubService.setWebPubSubClientToListenToOneDispatch(dispatchId);
      digitalDispatchWebPubSubService.onConnected(() => {
        setIsConnected(true);
      });
      digitalDispatchWebPubSubService.onDisconnected(() => {
        setIsConnected(false);
      });
      digitalDispatchWebPubSubService.onStopped(() => {
        setIsConnected(false);
      });
    } catch (error) {
      enqueueSnackbar(error?.message ?? 'Failed to fetch client access uri for live updates', {
        variant: 'error',
        preventDuplicate: true
      });
    } finally {
      setIsFetchingClientAccessUri(false);
    }
  };

  /**
   * Callback used when WebPubSub event handler receives a new group message
   * from the group set up to listen for any update on this particular dispatch;
   * in particular this is only listening to events when a new provider accepts/is
   * assigned
   * @param {Object} dispatchUpdate
   * @returns {void}
   */
  function addDispatchStatusUpdate(dispatchUpdate) {
    try {
      if (dispatchUpdate) {
        const { webPubSubUpdateType } = dispatchUpdate;
        if (DIGITAL_DISPATCH_LIVE_UPDATE_PROVIDER_ACCEPT === webPubSubUpdateType) {
          setProviderAcceptanceStatus(dispatchUpdate);
        }
      }
    } catch (error) {
      enqueueSnackbar(error?.message ?? 'Failed to parse incoming dispatch status update', {
        variant: 'error',
        preventDuplicate: true
      });
    }
  }

  /**
   * Joins the specified Webpubsub group for listening
   * to the dispatch with the passed in dispatchId
   * @returns {Promise<void>}
   */
  const startListeningForDigitalDispatchUpdates = async () => {
    try {
      await digitalDispatchWebPubSubService.startClient();
      await digitalDispatchWebPubSubService.joinGroup();
      digitalDispatchWebPubSubService.listenForGroupMessages(addDispatchStatusUpdate);
    } catch (error) {
      enqueueSnackbar(error?.message ? `${error?.message}` : 'Failed to start webpubsub client', {
        variant: 'error',
        preventDuplicate: true
      });
    }
  };

  /**
   * Closes the current connection
   * @returns {Promise<void>}
   */
  const closeConnection = async () => {
    await digitalDispatchWebPubSubService.stopClient();
  };

  /**
   * Stop the WebPubSubClient instance from running
   * and re-fetch the client access URI and set up the
   * service again
   * @returns {Promise<void>}
   */
  const startConnection = async () => {
    if (isConnected) {
      await digitalDispatchWebPubSubService.stopClient();
    }
    await setUpWebPubSubClientInstance(dispatchId);
  };

  useEffect(() => {
    const startConnectionOnMount = async () => {
      await startConnection();
      await startListeningForDigitalDispatchUpdates();
    };
    if (dispatchId && !isConnected) {
      startConnectionOnMount();
    }
  }, [dispatchId, isConnected]);

  return {
    isConnected,
    setIsConnected,
    startListeningForDigitalDispatchUpdates,
    closeConnection,
    isFetchingClientAccessUri,
    startConnection,
    providerAcceptanceStatus,
    setProviderAcceptanceStatus,
    webPubSubError,
    setWebPubSubError
  };
};

export default useDigitalDispatchTimerDispatchUpdate;
